/**
 * The notification settings surface, shared by every portal.
 *
 * Two portals want two different information architectures for the same data:
 * Cascade thinks in event types ("New Call", "New Form Entry") and MPF thinks in
 * Forms ("HPLA Application → Admin Notification, AutoResponder"). Rather than
 * force one layout on both, this exports one editor and two arrangements of it,
 * over one client interface — so the thing that is genuinely shared (what a
 * configuration is, what may be edited, how it is previewed and tested) has one
 * implementation.
 *
 * The list is the design. A notification is a row that answers three questions
 * at a glance — what it is, who gets it, is it on — and opens to reveal the
 * editor. Most visits to this screen are to check or flip a switch, not to
 * rewrite a template, so the template is the thing that hides.
 *
 * Deliberately not a drag-and-drop builder. The body is simple HTML in a
 * textarea with the available merge tags listed beside it, because the email
 * chrome — header, button, footer — is the system's and is not editable.
 */

import { useCallback, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Switch,
  cn,
} from '@command-center/ui-system';
import { Check, ChevronRight, Eye, Loader2, Send, X } from 'lucide-react';

/* ----------------------------------------------------------------- contract */

export interface NotificationMergeTag {
  tag: string;
  label: string;
  sample: string;
}

export interface NotificationTriggerOption {
  key: string;
  label: string;
  description: string;
  default: boolean;
}

export interface NotificationTrigger {
  key: string;
  name: string;
  description: string;
  scopes: ('tenant' | 'form')[];
  recipientMode: 'list' | 'submitter';
  sourceRecordType: string;
  mergeTags: NotificationMergeTag[];
  options: NotificationTriggerOption[];
  defaults: { subject: string; bodyHtml: string; ctaLabel: string };
}

export interface NotificationConfig {
  id: string | null;
  triggerKey: string;
  scopeType: 'tenant' | 'form';
  scopeId: string | null;
  name: string;
  enabled: boolean;
  subject: string;
  bodyHtml: string;
  recipientMode: 'list' | 'submitter';
  recipients: string[];
  fromName: string | null;
  fromEmail: string | null;
  replyTo: string | null;
  sendingDomain: string | null;
  options: Record<string, boolean>;
  updatedAt: string | null;
}

export interface NotificationFormOption {
  id: string;
  name: string;
  siteName?: string | null;
  submissionCount?: number;
}

export interface NotificationDelivery {
  id: string;
  trigger_key: string;
  source_record_type: string;
  source_record_id: string;
  recipient_email: string;
  status: string;
  skip_reason: string | null;
  subject: string | null;
  provider_message_id: string | null;
  error: string | null;
  is_test: boolean;
  created_at: string;
  sent_at: string | null;
}

/** The portal's saved outgoing-email identity, used unless a config overrides it. */
export interface NotificationSenderDefaults {
  fromName: string;
  fromEmail: string;
  replyTo: string;
}

export interface NotificationSettingsPayload {
  triggers: NotificationTrigger[];
  configs: NotificationConfig[];
  mailgunConfigured: boolean;
  sender: NotificationSenderDefaults;
}

export interface NotificationPreview {
  subject: string;
  html: string;
  text: string;
  warnings: string[];
}

/** Implemented per portal with that portal's own authenticated fetch helper. */
export interface NotificationSettingsClient {
  load(): Promise<NotificationSettingsPayload>;
  save(draft: NotificationDraft): Promise<{ config: NotificationConfig; warnings: string[] }>;
  preview(draft: NotificationDraft): Promise<NotificationPreview>;
  sendTest(draft: NotificationDraft & { to: string }): Promise<{ to: string }>;
  listForms(): Promise<NotificationFormOption[]>;
  listDeliveries(params: { status?: string }): Promise<NotificationDelivery[]>;
}

export interface NotificationDraft {
  triggerKey: string;
  scopeType: 'tenant' | 'form';
  scopeId: string | null;
  name: string;
  enabled: boolean;
  subject: string;
  bodyHtml: string;
  recipients: string[];
  options: Record<string, boolean>;
  /** Blank means "use this portal's saved outgoing-email settings". */
  fromName: string;
  fromEmail: string;
  replyTo: string;
  /** A verified Mailgun domain, when this tenant sends as itself. */
  sendingDomain: string;
}

/* -------------------------------------------------------------------- utils */

function draftFrom(
  trigger: NotificationTrigger,
  config: NotificationConfig | undefined,
  scope: { scopeType: 'tenant' | 'form'; scopeId: string | null },
): NotificationDraft {
  return {
    triggerKey: trigger.key,
    scopeType: scope.scopeType,
    scopeId: scope.scopeId,
    name: config?.name ?? trigger.name,
    enabled: config?.enabled ?? false,
    subject: config?.subject ?? trigger.defaults.subject,
    bodyHtml: config?.bodyHtml ?? trigger.defaults.bodyHtml,
    recipients: config?.recipients ?? [],
    options:
      config?.options ??
      Object.fromEntries(trigger.options.map((option) => [option.key, option.default])),
    fromName: config?.fromName ?? '',
    fromEmail: config?.fromEmail ?? '',
    replyTo: config?.replyTo ?? '',
    sendingDomain: config?.sendingDomain ?? '',
  };
}

function findConfig(
  configs: NotificationConfig[],
  triggerKey: string,
  scopeId: string | null,
): NotificationConfig | undefined {
  return configs.find(
    (config) => config.triggerKey === triggerKey && (config.scopeId ?? null) === scopeId,
  );
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong.';
}

/**
 * Stand-in for a Worker that predates the sender defaults in the settings
 * payload. Costs nothing, and means a version skew between the API and the
 * bundle shows up as empty placeholder text rather than a blank screen.
 */
const EMPTY_SENDER: NotificationSenderDefaults = { fromName: '', fromEmail: '', replyTo: '' };

/**
 * The one line under the title.
 *
 * Once recipients exist, who receives this is more useful than a description of
 * what it is — the operator already knows what "New Call" means, and is here to
 * check where it goes.
 */
function summarize(trigger: NotificationTrigger, draft: NotificationDraft): string {
  if (trigger.recipientMode === 'submitter') {
    return 'Replies to the address on the Form Entry';
  }
  const { recipients } = draft;
  if (recipients.length === 0) return trigger.description;
  if (recipients.length <= 2) return `Sent to ${recipients.join(' and ')}`;
  return `Sent to ${recipients[0]} and ${recipients.length - 1} others`;
}

/* ------------------------------------------------------------------- editor */

interface EditorProps {
  client: NotificationSettingsClient;
  trigger: NotificationTrigger;
  config: NotificationConfig | undefined;
  scopeType: 'tenant' | 'form';
  scopeId: string | null;
  mailgunConfigured: boolean;
  senderDefaults: NotificationSenderDefaults;
  onSaved: (config: NotificationConfig) => void;
}

export function NotificationConfigEditor({
  client,
  trigger,
  config,
  scopeType,
  scopeId,
  mailgunConfigured,
  senderDefaults,
  onSaved,
}: EditorProps) {
  const bodyId = useId();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<NotificationDraft>(() =>
    draftFrom(trigger, config, { scopeType, scopeId }),
  );
  const [recipientText, setRecipientText] = useState(() => (config?.recipients ?? []).join(', '));
  const [status, setStatus] = useState<'idle' | 'saving' | 'testing' | 'toggling'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [preview, setPreview] = useState<NotificationPreview | null>(null);
  const [testTo, setTestTo] = useState('');

  useEffect(() => {
    setDraft(draftFrom(trigger, config, { scopeType, scopeId }));
    setRecipientText((config?.recipients ?? []).join(', '));
  }, [config, trigger, scopeType, scopeId]);

  const recipients = useMemo(
    () =>
      recipientText
        .split(/[,\n;]+/)
        .map((value) => value.trim())
        .filter(Boolean),
    [recipientText],
  );

  const body = { ...draft, recipients };

  const run = useCallback(
    async (kind: 'saving' | 'testing' | 'toggling', action: () => Promise<void>) => {
      setStatus(kind);
      setError(null);
      setNotice(null);
      try {
        await action();
      } catch (caught) {
        setError(message(caught));
        throw caught;
      } finally {
        setStatus('idle');
      }
    },
    [],
  );

  const save = () =>
    run('saving', async () => {
      const result = await client.save(body);
      setWarnings(result.warnings);
      setNotice('Saved.');
      onSaved(result.config);
    }).catch(() => undefined);

  /**
   * A switch that needs a separate Save is a switch that lies. Flipping a
   * collapsed row persists immediately — but against the *saved* configuration
   * rather than the open draft, so an unsaved template edit is never committed
   * as a side effect of turning something on.
   */
  const toggle = (enabled: boolean) => {
    setDraft((current) => ({ ...current, enabled }));
    if (open) return;
    run('toggling', async () => {
      const saved = { ...draftFrom(trigger, config, { scopeType, scopeId }), enabled };
      const result = await client.save(saved);
      onSaved(result.config);
    }).catch(() => {
      // The server refused — usually "add a recipient first". Put the switch
      // back and open the row, so the fix is where the eye already is.
      setDraft((current) => ({ ...current, enabled: !enabled }));
      setOpen(true);
    });
  };

  const showPreview = () =>
    run('saving', async () => {
      setPreview(await client.preview(body));
    }).catch(() => undefined);

  const sendTest = () =>
    run('testing', async () => {
      const result = await client.sendTest({ ...body, to: testTo.trim() });
      setNotice(`Test message sent to ${result.to}.`);
    }).catch(() => undefined);

  const submitterAddressed = trigger.recipientMode === 'submitter';
  const busy = status !== 'idle';

  return (
    <div className={cn(draft.enabled && 'bg-selection')}>
      <div className="flex items-center gap-3 pr-4">
        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          aria-controls={bodyId}
          className="flex min-w-0 flex-1 items-center gap-3 py-3 pl-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        >
          <ChevronRight
            aria-hidden
            className={cn(
              'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-150 motion-reduce:transition-none',
              open && 'rotate-90',
            )}
          />
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium text-foreground">
              {trigger.name}
            </span>
            <span className="block truncate text-xs text-muted-foreground">
              {summarize(trigger, body)}
            </span>
          </span>
        </button>

        <span className="flex shrink-0 items-center gap-2">
          {status === 'toggling' && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden />
          )}
          <Switch
            checked={draft.enabled}
            onCheckedChange={toggle}
            disabled={busy}
            aria-label={`${trigger.name} notification`}
          />
        </span>
      </div>

      {/* A collapsed row still has to report a refused toggle. */}
      {!open && error && <p className="px-4 pb-3 pl-11 text-xs text-destructive">{error}</p>}

      {open && (
        <div
          id={bodyId}
          className="flex flex-col gap-5 border-t border-border/60 px-4 py-5 sm:pl-11"
        >
          <p className="text-xs text-muted-foreground">{trigger.description}</p>

          {!mailgunConfigured && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              Outgoing email is not configured for this portal yet, so nothing will be delivered
              while this is on. Settings can still be saved.
            </p>
          )}

          <Field label="Recipients">
            {submitterAddressed ? (
              <p className="text-xs text-muted-foreground">
                Sent to the email address stored on the Form Entry. The recipient is resolved on the
                server from the submission itself and cannot be set here.
              </p>
            ) : (
              <>
                <Input
                  value={recipientText}
                  onChange={(event) => setRecipientText(event.target.value)}
                  placeholder="ops@example.com, someone-else@example.com"
                  aria-label="Recipients"
                />
                <Hint>Separate addresses with commas.</Hint>
              </>
            )}
          </Field>

          {trigger.options.map((option) => (
            <label key={option.key} className="flex items-start gap-3">
              <Switch
                checked={draft.options[option.key] ?? option.default}
                onCheckedChange={(value) =>
                  setDraft((current) => ({
                    ...current,
                    options: { ...current.options, [option.key]: value },
                  }))
                }
                aria-label={option.label}
              />
              <span className="min-w-0">
                <span className="block text-sm text-foreground">{option.label}</span>
                <span className="block text-xs text-muted-foreground">{option.description}</span>
              </span>
            </label>
          ))}

          <Field label="Subject">
            <Input
              value={draft.subject}
              onChange={(event) =>
                setDraft((current) => ({ ...current, subject: event.target.value }))
              }
              aria-label="Subject"
            />
          </Field>

          <Field label="Email body">
            <textarea
              value={draft.bodyHtml}
              onChange={(event) =>
                setDraft((current) => ({ ...current, bodyHtml: event.target.value }))
              }
              rows={10}
              aria-label="Email body"
              className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-[13px] leading-relaxed text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <Hint>
              Simple HTML. The header, {trigger.defaults.ctaLabel ? 'button, ' : ''}and footer are
              added automatically.
            </Hint>
          </Field>

          <Field label="Merge tags">
            <div className="flex flex-wrap gap-1.5">
              {trigger.mergeTags.map((tag) => (
                <button
                  key={tag.tag}
                  type="button"
                  title={`${tag.label} — click to insert`}
                  onClick={() =>
                    setDraft((current) => ({
                      ...current,
                      bodyHtml: `${current.bodyHtml}{{${tag.tag}}}`,
                    }))
                  }
                  className="rounded border border-border bg-muted/40 px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {`{{${tag.tag}}}`}
                </button>
              ))}
            </div>
          </Field>

          <details className="rounded-md border border-border px-3 py-2">
            <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
              Sender
            </summary>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="From name">
                <Input
                  value={draft.fromName}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, fromName: event.target.value }))
                  }
                  placeholder={senderDefaults.fromName || 'Not set'}
                  aria-label="From name"
                />
              </Field>
              <Field label="From address">
                <Input
                  value={draft.fromEmail}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, fromEmail: event.target.value }))
                  }
                  placeholder={senderDefaults.fromEmail || 'Not set'}
                  aria-label="From address"
                />
              </Field>
              <Field label="Reply-To">
                <Input
                  value={draft.replyTo}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, replyTo: event.target.value }))
                  }
                  placeholder={senderDefaults.replyTo || 'None'}
                  aria-label="Reply-To"
                />
              </Field>
              <Field label="Sending domain">
                <Input
                  value={draft.sendingDomain}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, sendingDomain: event.target.value }))
                  }
                  placeholder="Portal default"
                  aria-label="Sending domain"
                />
              </Field>
            </div>
            {/*
              Showing the resolved values as placeholders is the point: an
              operator can see which sender is actually in force instead of
              taking the words "portal default" on faith.
            */}
            <Hint>
              Greyed values come from this portal&rsquo;s email settings and are used unless you
              type something here. A sending domain must already be verified with the email
              provider; naming an unverified one makes sends fail rather than changing where they
              come from.
            </Hint>
          </details>

          {warnings.length > 0 && (
            <ul className="flex flex-col gap-1 text-xs text-amber-700 dark:text-amber-300">
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
            <Button size="sm" onClick={save} disabled={busy}>
              {status === 'saving' && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={showPreview} disabled={busy}>
              <Eye className="mr-1.5 h-3.5 w-3.5" /> Preview
            </Button>
            <span className="flex items-center gap-1.5">
              <Input
                value={testTo}
                onChange={(event) => setTestTo(event.target.value)}
                placeholder="you@example.com"
                className="h-8 w-52"
                aria-label="Send a test to"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={sendTest}
                disabled={busy || !testTo.trim()}
              >
                {status === 'testing' ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="mr-1.5 h-3.5 w-3.5" />
                )}
                Send Test
              </Button>
            </span>
          </div>

          {/*
            Send Test goes out from this portal's Worker; the real notification
            is sent by the Worker that ingests the event. They can be configured
            differently, so a successful test is not by itself proof that live
            delivery works — the delivery log is.
          */}
          <Hint>
            A successful test proves the template. Live delivery happens where the event is
            ingested — check the delivery log after the first real one; an{' '}
            <span className="font-mono">email_provider_not_configured</span> entry there means that
            Worker is missing its email credential.
          </Hint>

          {error && <p className="text-xs text-destructive">{error}</p>}
          {notice && <p className="text-xs text-green-700 dark:text-green-400">{notice}</p>}
        </div>
      )}

      <Dialog open={Boolean(preview)} onOpenChange={(isOpen) => !isOpen && setPreview(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-sm">{preview?.subject}</DialogTitle>
          </DialogHeader>
          {preview?.warnings.length ? (
            <ul className="flex flex-col gap-1 text-xs text-amber-700 dark:text-amber-300">
              {preview.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          ) : null}
          {/*
            The preview is rendered in a sandboxed iframe with no scripts and no
            same-origin access: the server already strips executable markup, and
            this makes a preview incapable of touching the portal even if
            something got through.
          */}
          <iframe
            title="Email preview"
            sandbox=""
            srcDoc={preview?.html ?? ''}
            className="h-[60vh] w-full rounded-md border border-border bg-white"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col">
      <span className="mb-1.5 text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

function Hint({ children }: { children: ReactNode }) {
  return <p className="mt-1.5 text-xs text-muted-foreground">{children}</p>;
}

/** One bordered list rather than a stack of cards, so the rows read as a set. */
function RowList({ children }: { children: ReactNode }) {
  return (
    <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
      {children}
    </div>
  );
}

function GroupHeading({ children }: { children: ReactNode }) {
  return (
    <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </h2>
  );
}

/* ------------------------------------------------------------ arrangements */

function useNotificationSettings(client: NotificationSettingsClient) {
  const [payload, setPayload] = useState<NotificationSettingsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    client
      .load()
      .then((next) => !cancelled && setPayload(next))
      .catch((caught) => !cancelled && setError(message(caught)));
    return () => {
      cancelled = true;
    };
  }, [client]);

  const onSaved = useCallback((config: NotificationConfig) => {
    setPayload((current) => {
      if (!current) return current;
      const rest = current.configs.filter(
        (existing) =>
          !(
            existing.triggerKey === config.triggerKey &&
            (existing.scopeId ?? null) === (config.scopeId ?? null)
          ),
      );
      return { ...current, configs: [...rest, config] };
    });
  }, []);

  return { payload, error, onSaved };
}

/**
 * Cascade's arrangement: one section per kind of thing that happens.
 */
export function PortalNotificationSettings({ client }: { client: NotificationSettingsClient }) {
  const { payload, error, onSaved } = useNotificationSettings(client);

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!payload) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const senderDefaults = payload.sender ?? EMPTY_SENDER;
  const tenantTriggers = payload.triggers.filter((trigger) => trigger.scopes.includes('tenant'));
  const groups = [
    { title: 'Calls', triggers: tenantTriggers.filter((t) => t.sourceRecordType === 'call') },
    {
      title: 'Forms',
      triggers: tenantTriggers.filter((t) => t.sourceRecordType === 'form_submission'),
    },
  ].filter((group) => group.triggers.length > 0);

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      {groups.map((group) => (
        <section key={group.title} className="flex flex-col gap-2">
          <GroupHeading>{group.title}</GroupHeading>
          <RowList>
            {group.triggers.map((trigger) => (
              <NotificationConfigEditor
                key={trigger.key}
                client={client}
                trigger={trigger}
                config={findConfig(payload.configs, trigger.key, null)}
                scopeType="tenant"
                scopeId={null}
                mailgunConfigured={payload.mailgunConfigured}
                senderDefaults={senderDefaults}
                onSaved={onSaved}
              />
            ))}
          </RowList>
        </section>
      ))}
    </div>
  );
}

/**
 * MPF's arrangement: pick a Form, then configure the two things that Form can
 * do. Admin Notification and AutoResponder are independent by construction —
 * they are separate configurations, not two switches on one.
 */
export function FormNotificationSettings({ client }: { client: NotificationSettingsClient }) {
  const { payload, error, onSaved } = useNotificationSettings(client);
  const [forms, setForms] = useState<NotificationFormOption[] | null>(null);
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    client
      .listForms()
      .then((next) => {
        if (cancelled) return;
        setForms(next);
        setSelectedFormId((current) => current ?? next[0]?.id ?? null);
      })
      .catch(() => !cancelled && setForms([]));
    return () => {
      cancelled = true;
    };
  }, [client]);

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!payload || !forms) return <p className="text-sm text-muted-foreground">Loading…</p>;

  if (forms.length === 0) {
    return (
      <p className="max-w-2xl text-sm text-muted-foreground">
        No Forms have been seen yet. A Form appears here the first time a submission arrives through
        the shared form ingestion endpoint, and can be configured from that point on.
      </p>
    );
  }

  const senderDefaults = payload.sender ?? EMPTY_SENDER;
  const selected = forms.find((form) => form.id === selectedFormId) ?? forms[0]!;
  const formTriggers = payload.triggers.filter((trigger) => trigger.scopes.includes('form'));

  return (
    <div className="flex flex-col gap-4 lg:flex-row">
      <nav className="flex shrink-0 flex-col gap-0.5 lg:w-64" aria-label="Forms">
        {forms.map((form) => (
          <button
            key={form.id}
            type="button"
            onClick={() => setSelectedFormId(form.id)}
            aria-current={form.id === selected.id}
            className={cn(
              'rounded-md px-3 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              form.id === selected.id
                ? 'bg-selection font-medium text-foreground'
                : 'text-foreground hover:bg-muted/60',
            )}
          >
            <span className="block truncate">{form.name}</span>
            {form.siteName ? (
              <span className="block truncate text-xs text-muted-foreground">{form.siteName}</span>
            ) : null}
          </button>
        ))}
      </nav>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <GroupHeading>{selected.name}</GroupHeading>
        <RowList>
          {formTriggers.map((trigger) => (
            <NotificationConfigEditor
              key={`${selected.id}:${trigger.key}`}
              client={client}
              trigger={trigger}
              config={findConfig(payload.configs, trigger.key, selected.id)}
              scopeType="form"
              scopeId={selected.id}
              mailgunConfigured={payload.mailgunConfigured}
              senderDefaults={senderDefaults}
              onSaved={onSaved}
            />
          ))}
        </RowList>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ delivery log */

const STATUS_TONE: Record<string, string> = {
  sent: 'text-green-700 dark:text-green-400',
  failed: 'text-destructive',
  skipped: 'text-muted-foreground',
  pending: 'text-amber-700 dark:text-amber-300',
};

/**
 * Transactional diagnostics: what was sent, to whom, and — just as importantly —
 * what was not sent and why. Not analytics; there is nothing here to trend.
 */
export function NotificationDeliveryLog({ client }: { client: NotificationSettingsClient }) {
  const [rows, setRows] = useState<NotificationDelivery[] | null>(null);
  const [status, setStatus] = useState('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    client
      .listDeliveries({ status })
      .then((next) => !cancelled && setRows(next))
      .catch((caught) => !cancelled && setError(message(caught)));
    return () => {
      cancelled = true;
    };
  }, [client, status]);

  if (error) return <p className="text-sm text-destructive">{error}</p>;

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap gap-1.5">
        {['all', 'sent', 'failed', 'skipped', 'pending'].map((value) => (
          <Button
            key={value}
            size="sm"
            variant={status === value ? 'default' : 'outline'}
            onClick={() => setStatus(value)}
          >
            {value}
          </Button>
        ))}
      </div>

      {!rows ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No deliveries recorded yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-2 font-medium">When</th>
                <th className="px-4 py-2 font-medium">Notification</th>
                <th className="px-4 py-2 font-medium">Recipient</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {rows.map((row) => (
                <tr key={row.id} className="align-top">
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {new Date(row.sent_at ?? row.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-2">
                    <span className="block font-mono text-xs">{row.trigger_key}</span>
                    <span className="block text-xs text-muted-foreground">
                      {row.source_record_type} · {row.source_record_id}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs">{row.recipient_email}</td>
                  <td className={cn('px-4 py-2 text-xs font-medium', STATUS_TONE[row.status])}>
                    <span className="inline-flex items-center gap-1">
                      {row.status === 'sent' ? (
                        <Check className="h-3 w-3" />
                      ) : row.status === 'failed' ? (
                        <X className="h-3 w-3" />
                      ) : null}
                      {row.status}
                      {row.is_test ? ' (test)' : ''}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {row.error ?? row.skip_reason ?? row.provider_message_id ?? row.subject ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
